import { memo, useState } from "react";
import { FormattedMessage } from "react-intl";

import { LooseLeafCertificates, type NotarialActs, type PageTypes } from "graphql_globals";
import { DOC_CATEGORIES } from "constants/document";
import WorkflowModalV2 from "common/modals/workflow_modal/v2";
import Button from "common/core/button";
import { CheckboxWithLabel } from "common/form/inputs/checkbox";
import { looseLeafPageLabel } from "util/document";
import { useFeatureFlag } from "common/feature_gating";

import Styles from "./loose_leaf_modal.module.scss";

type Props = {
  onSave: (
    selectedLLPs: Set<LooseLeafCertificates>,
    newestLLP?: LooseLeafCertificates,
  ) => Promise<unknown>;
  onClose: () => void;
  currentDocumentNode: {
    classification: null | { category: string | null };
    versionedLooseLeafCertificates: { actType: LooseLeafCertificates }[];
  };
  availableLooseLeafs: LooseLeafCertificates[][];
  initLLP?: NotarialActs;
};

function isPS1583Classification(
  classification: Props["currentDocumentNode"]["classification"],
  looseLeafOptions: LooseLeafCertificates[][],
): boolean {
  return (
    classification?.category?.toUpperCase() === DOC_CATEGORIES.PS1583_ATTESTATION &&
    looseLeafOptions.some((options) => options.includes(LooseLeafCertificates.ATTESTATION))
  );
}

function sortLooseLeafLabel(a: LooseLeafCertificates, b: LooseLeafCertificates) {
  return (looseLeafPageLabel(a as unknown as PageTypes) || "").localeCompare(
    looseLeafPageLabel(b as unknown as PageTypes) || "",
  );
}

function LooseLeafModal({
  availableLooseLeafs,
  onSave,
  onClose,
  initLLP,
  currentDocumentNode,
}: Props) {
  const currentDocumentsLooseLeafTypes = currentDocumentNode.versionedLooseLeafCertificates.map(
    (llc) => llc.actType,
  );
  const looseLeafOptions = useFeatureFlag("show-affidavit-loose-leaf")
    ? availableLooseLeafs
    : availableLooseLeafs.filter((l) => !l.includes(LooseLeafCertificates.AFFIDAVIT));
  const isPS1583 = isPS1583Classification(currentDocumentNode.classification, looseLeafOptions);
  const [selectedLLPs, setSelectedLLPs] = useState(() => {
    const state = new Set(currentDocumentsLooseLeafTypes);
    if (initLLP && initLLP in LooseLeafCertificates) {
      state.add(initLLP as unknown as LooseLeafCertificates);
    }
    if (isPS1583) {
      state.add(LooseLeafCertificates.ATTESTATION);
    }
    return state;
  });

  const [isSavingLLP, setIsSavingLLP] = useState(false);

  const saveLLP = () => {
    setIsSavingLLP(true);
    const originalLooseLeafs = new Set(currentDocumentsLooseLeafTypes);
    const [newestLLP] = Array.from(selectedLLPs).filter((llp) => !originalLooseLeafs.has(llp));
    onSave(selectedLLPs, newestLLP).catch(() => setIsSavingLLP(false));
  };
  return (
    <WorkflowModalV2
      title={
        <FormattedMessage
          id="51fbed89-423a-47f3-a790-0add00e83da8"
          defaultMessage="Choose Certificate Types"
        />
      }
      closeBehavior={{ tag: "with-button", onClose }}
      headerSeparator
      buttons={[
        <Button key="dismiss" onClick={onClose} buttonColor="dark" variant="tertiary">
          <FormattedMessage id="9329c338-95f3-41b3-8e78-0e5b91372956" defaultMessage="Cancel" />
        </Button>,
        <Button
          key="update"
          automationId="add-documents-button"
          onClick={saveLLP}
          buttonColor="action"
          variant="primary"
          isLoading={isSavingLLP}
        >
          <FormattedMessage id="c34bcd81-557e-440b-8e2c-a878ade7adb3" defaultMessage="Save" />
        </Button>,
      ]}
    >
      <div className={Styles.looseLeafModal}>
        {isPS1583 && (
          <FormattedMessage
            id="a129e6ee-80f4-4f0e-a860-bed4ba3825ee"
            defaultMessage="PS1583 forms only accept an attestation"
            tagName="p"
          />
        )}
        <ul>
          {looseLeafOptions.map((looseLeafs) => {
            return looseLeafs.sort(sortLooseLeafLabel).map((llpActType) => {
              return (
                <li key={llpActType}>
                  <CheckboxWithLabel
                    checked={selectedLLPs.has(llpActType)}
                    onChange={() => {
                      const newSelectedLLPs = new Set(selectedLLPs);
                      if (newSelectedLLPs.has(llpActType)) {
                        newSelectedLLPs.delete(llpActType);
                      } else {
                        newSelectedLLPs.add(llpActType);
                      }

                      setSelectedLLPs(newSelectedLLPs);
                    }}
                    label={looseLeafPageLabel(llpActType as unknown as PageTypes)}
                    automationId={`loose-leaf-${llpActType}`}
                  />
                </li>
              );
            });
          })}
        </ul>
      </div>
    </WorkflowModalV2>
  );
}

export default memo(LooseLeafModal);
